/**
 * Adapter: maps backend Trip docs (CMS schema) to the display shape used by
 * ExpeditionsPage / ClassesPage / detail pages / home cards.
 *
 * Backend Trip:
 *   { trip_id, kind, category, title, title_gr, description, description_gr,
 *     start_date (YYYY-MM-DD), duration_days, capacity, member_price, guest_price,
 *     deposit_amount, requires_medical, image, images[], difficulty, altitude_m,
 *     distance_km, published, seats_remaining, seats_taken }
 *
 * Display Trip (legacy, kept for visual parity):
 *   { id, trip_id, title, title_gr, description, description_gr, image, images[],
 *     date, price, memberPrice, difficulty, category, duration, distance,
 *     ascent, descent, elevation, availableSeats, totalSeats, status,
 *     requires_medical }
 */
import { useEffect, useState, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_GR = ['Ιανουαρίου', 'Φεβρουαρίου', 'Μαρτίου', 'Απριλίου', 'Μαΐου', 'Ιουνίου', 'Ιουλίου', 'Αυγούστου', 'Σεπτεμβρίου', 'Οκτωβρίου', 'Νοεμβρίου', 'Δεκεμβρίου'];
const MONTHS_SHORT_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_SHORT_GR = ['Ιαν', 'Φεβ', 'Μαρ', 'Απρ', 'Μαΐ', 'Ιουν', 'Ιουλ', 'Αυγ', 'Σεπ', 'Οκτ', 'Νοε', 'Δεκ'];

const formatDateRange = (startStr, durationDays = 1, endStr = null, lang = 'en') => {
  if (!startStr) return '';
  const start = new Date(startStr + 'T00:00:00');
  if (Number.isNaN(start.getTime())) return startStr;
  const months = lang === 'gr' ? MONTHS_GR : MONTHS_EN;
  const monthsShort = lang === 'gr' ? MONTHS_SHORT_GR : MONTHS_SHORT_EN;
  const fmt = (d) => {
    const day = d.getDate();
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return lang === 'gr' ? `${day} ${month} ${year}` : `${month} ${day}, ${year}`;
  };
  // Short form for cross-month / cross-year ranges — language-aware ordering
  const fmtShort = (d) =>
    lang === 'gr'
      ? `${d.getDate()} ${monthsShort[d.getMonth()]}`
      : `${monthsShort[d.getMonth()]} ${d.getDate()}`;

  // If an explicit end_date is provided (courses that span weeks), use it verbatim.
  const end = endStr ? new Date(endStr + 'T00:00:00') : null;
  if (end && !Number.isNaN(end.getTime()) && end.getTime() !== start.getTime()) {
    if (start.getFullYear() === end.getFullYear()) {
      if (start.getMonth() === end.getMonth()) {
        return lang === 'gr'
          ? `${start.getDate()}-${end.getDate()} ${months[start.getMonth()]} ${start.getFullYear()}`
          : `${months[start.getMonth()]} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
      }
      return lang === 'gr'
        ? `${fmtShort(start)} - ${fmtShort(end)} ${start.getFullYear()}`
        : `${fmtShort(start)} - ${fmtShort(end)}, ${start.getFullYear()}`;
    }
    return `${fmt(start)} – ${fmt(end)}`;
  }

  // Else use start + duration_days
  if (durationDays <= 1) return fmt(start);
  const computedEnd = new Date(start);
  computedEnd.setDate(computedEnd.getDate() + (durationDays - 1));
  if (start.getMonth() === computedEnd.getMonth()) {
    return lang === 'gr'
      ? `${start.getDate()}-${computedEnd.getDate()} ${months[start.getMonth()]} ${start.getFullYear()}`
      : `${months[start.getMonth()]} ${start.getDate()}-${computedEnd.getDate()}, ${start.getFullYear()}`;
  }
  return lang === 'gr'
    ? `${fmtShort(start)} - ${fmtShort(computedEnd)} ${start.getFullYear()}`
    : `${fmtShort(start)} - ${fmtShort(computedEnd)}, ${start.getFullYear()}`;
};

const formatDuration = (days, hours, lang = 'en', timeWindow = '') => {
  const d = Number(days) || 0;
  const h = Number(hours) || 0;
  const parts = [];
  if (d > 0) {
    if (d >= 30) parts.push(lang === 'gr' ? `${Math.round(d / 7)} εβδομάδες` : `${Math.round(d / 7)} weeks`);
    else if (d === 1) parts.push(lang === 'gr' ? '1 ημέρα' : '1 day');
    else parts.push(lang === 'gr' ? `${d} ημέρες` : `${d} days`);
  }
  if (h > 0) {
    parts.push(lang === 'gr' ? `${h} ώρες` : `${h}h`);
  }
  // Fallbacks for sub-day events without explicit duration: prefer the start–end
  // time window (e.g. "18:00–22:00 (4h)") rather than misleading "1 day".
  if (!parts.length) {
    if (timeWindow) return timeWindow;
    return lang === 'gr' ? '—' : '—';
  }
  return parts.join(' ');
};

// "08:00–16:00 (8h)" — used on cards when both times set on the trip.
const formatTimeWindow = (startTime, endTime, lang = 'en') => {
  if (!startTime || !endTime) return '';
  const [sh, sm = 0] = startTime.split(':').map(Number);
  const [eh, em = 0] = endTime.split(':').map(Number);
  const startMins = sh * 60 + sm;
  const endMins = eh * 60 + em;
  const diff = endMins - startMins;
  if (Number.isNaN(diff) || diff <= 0) return `${startTime}–${endTime}`;
  const hours = Math.floor(diff / 60);
  const mins = diff % 60;
  const totalLabel = mins
    ? `${hours}${lang === 'gr' ? 'ω' : 'h'} ${mins}${lang === 'gr' ? 'λ' : 'm'}`
    : `${hours}${lang === 'gr' ? 'ω' : 'h'}`;
  return `${startTime}–${endTime} (${totalLabel})`;
};

const formatPriceRange = (memberPrice, guestPrice, lang = 'en') => {
  const m = Number(memberPrice) || 0;
  const g = Number(guestPrice) || 0;
  const free = lang === 'gr' ? 'Δωρεάν' : 'Free';
  if (m === 0 && g === 0) return free;
  if (m === g) return `€${m}`;
  return `€${m} - €${g}`;
};

const formatMemberPrice = (memberPrice, lang = 'en') => {
  const m = Number(memberPrice) || 0;
  if (m === 0) return lang === 'gr' ? 'Δωρεάν' : 'Free';
  return `€${m}`;
};

// Iter440 — When the trip uses a variant pricing matrix (difficulty and/or
// accommodation), the flat member/guest fields on the trip may be 0 while the
// real prices live in `price_matrix` rows. Resolve the displayable min/max
// from those rows so the listing card and detail hero show the correct
// price (or a "από €X" range) instead of a misleading "Δωρεάν για όλους".
const _resolveMatrixPricing = (t) => {
  const axes = t.pricing_axes || {};
  const enabled = !!(axes.accommodation_enabled || axes.difficulty_enabled);
  const matrix = Array.isArray(t.price_matrix) ? t.price_matrix : [];
  if (!enabled || matrix.length === 0) return null;
  const mNums = matrix.map((r) => Number(r.member_price) || 0).filter((n) => n > 0);
  const gNums = matrix.map((r) => Number(r.guest_price) || 0).filter((n) => n > 0);
  if (mNums.length === 0 && gNums.length === 0) return null;
  return {
    memberMin: mNums.length ? Math.min(...mNums) : 0,
    memberMax: mNums.length ? Math.max(...mNums) : 0,
    guestMin: gNums.length ? Math.min(...gNums) : 0,
    guestMax: gNums.length ? Math.max(...gNums) : 0,
  };
};

const _labelPrice = (min, max, lang) => {
  if (!min && !max) return lang === 'gr' ? 'Δωρεάν' : 'Free';
  if (min === max) return `€${min}`;
  return lang === 'gr' ? `από €${min}` : `from €${min}`;
};

export const adaptTrip = (t, lang = 'en') => {
  // Iter440 — Resolve variant-matrix prices up front so downstream display
  // fields (price/memberPrice/guestPriceLabel + isFree flags + numeric _num
  // helpers) all share the same source of truth. Falls back to flat
  // member_price/guest_price when no matrix is in use.
  const mx = _resolveMatrixPricing(t);
  const flatM = Number(t.member_price) || 0;
  const flatG = Number(t.guest_price) || 0;
  const memberMin = mx ? mx.memberMin : flatM;
  const memberMax = mx ? mx.memberMax : flatM;
  const guestMin  = mx ? mx.guestMin  : flatG;
  const guestMax  = mx ? mx.guestMax  : flatG;
  return ({
  // Prefer slug in URLs (SEO); fall back to trip_id for legacy trips without a slug.
  id: t.slug || t.trip_id,
  trip_id: t.trip_id,
  slug: t.slug || null,
  pickup_options: t.pickup_options || [],
  leaders: t.leaders || [],
  title: t.title || '',
  title_gr: t.title_gr || t.title || '',
  description: t.description || '',
  description_gr: t.description_gr || t.description || '',
  itinerary: t.itinerary || '',
  itinerary_gr: t.itinerary_gr || '',
  included: t.included || '',
  included_gr: t.included_gr || '',
  requirements: t.requirements || '',
  requirements_gr: t.requirements_gr || '',
  terms: t.terms || '',
  terms_gr: t.terms_gr || '',
  image: t.image || '',
  images: (t.images && t.images.length) ? t.images : (t.image ? [t.image] : []),
  date: formatDateRange(t.start_date, t.duration_days, t.end_date, lang),
  start_date: t.start_date || '',
  end_date: t.end_date || '',
  // Combined range display (used on some cards + fallback for guest tile)
  price: mx ? _labelPrice(Math.min(memberMin, guestMin), Math.max(memberMax, guestMax), lang)
            : formatPriceRange(t.member_price, t.guest_price, lang),
  memberPrice: mx ? _labelPrice(memberMin, memberMax, lang) : formatMemberPrice(t.member_price, lang),
  guestPriceLabel: mx ? _labelPrice(guestMin, guestMax, lang) : `€${flatG}`,
  difficulty: t.difficulty || '',
  category: t.category || '',
  championship: t.championship || null,
  location_label: t.location_label || t.location_label_gr || '',
  location_label_gr: t.location_label_gr || '',
  duration: formatDuration(t.duration_days, t.duration_hours, lang, formatTimeWindow(t.start_time, t.end_time, lang)),
  duration_days: t.duration_days || 0,
  duration_hours: t.duration_hours || 0,
  start_time: t.start_time || '',
  end_time: t.end_time || '',
  timeWindow: formatTimeWindow(t.start_time, t.end_time, lang),
  deposit_options: Array.isArray(t.deposit_options) ? t.deposit_options : [],
  // Iter440 — expose matrix flags so consumers (BookingPanel etc.) can
  // detect a variant-priced trip without re-deriving the axes locally.
  pricing_axes: t.pricing_axes || null,
  price_matrix: Array.isArray(t.price_matrix) ? t.price_matrix : [],
  has_price_matrix: !!mx,
  // Iter441 — Informational lodging picker (no pricing impact). Passed
  // through to BookingPanel so it can render the radio group + disclaimer.
  lodging_options: Array.isArray(t.lodging_options) ? t.lodging_options : [],
  // "Free" pills — recomputed against the resolved min prices so a trip
  // whose flat fields are 0 but has non-zero matrix rows is NOT flagged
  // as free.
  isFreeForAll: memberMin === 0 && memberMax === 0 && guestMin === 0 && guestMax === 0,
  isFreeForMembers: memberMin === 0 && memberMax === 0 && (guestMin > 0 || guestMax > 0),
  distance: t.distance_km ? `${t.distance_km}km` : '',
  ascent: t.altitude_m ? `${t.altitude_m}m` : '',
  descent: t.descent_m ? `${t.descent_m}m` : (t.altitude_m ? `${t.altitude_m}m` : ''),
  elevation: t.altitude_m ? `${t.altitude_m}m` : '',
  altitude_m: t.altitude_m || 0,
  descent_m: t.descent_m || 0,
  availableSeats: t.seats_remaining != null ? t.seats_remaining : (t.capacity || 0),
  totalSeats: t.capacity || 0,
  status: (() => {
    if (t.cancelled) return 'cancelled';
    if ((t.seats_remaining ?? 0) > 0) return 'available';
    // Sold out from the regular-seat perspective. Iter118: if the waiting
    // list is enabled AND has room, surface 'waiting_list' so the cards
    // render the amber WL badge instead of the red SOLD OUT badge.
    if (t.waiting_list_available) return 'waiting_list';
    return 'sold_out';
  })(),
  cancelled: !!t.cancelled,
  kind: t.kind,
  leader_contacts: Array.isArray(t.leader_contacts) ? t.leader_contacts : [],
  trainers: Array.isArray(t.trainers) ? t.trainers : [],
  requires_medical: !!t.requires_medical,
  member_price_num: memberMin,
  guest_price_num: guestMin || guestMax,
  deposit_enabled: t.deposit_enabled !== false,
  location_preset: t.location_preset || null,
  location_label: t.location_label || '',
  location_label_gr: t.location_label_gr || '',
  location_maps_url: t.location_maps_url || '',
  // Iter111 — automatic booking deadline (Sat 18:00 Hymettus / Tue 18:00 Greece) + closed flag
  booking_deadline: t.booking_deadline || null,
  bookings_closed: !!t.bookings_closed,
  // Price-TBA — price not decided yet: trip stays visible but not bookable.
  pricing_pending: !!t.pricing_pending,
  price_tbd: !!t.price_tbd,
  // Iter118 — waiting-list state (the trip endpoint exposes the limit + enabled flag)
  waiting_list_enabled: t.waiting_list_enabled !== false,
  waiting_list_limit: t.waiting_list_limit || 10,
  waiting_list_count: t.waiting_list_count || 0,
  waiting_list_available: !!t.waiting_list_available,
  // Iter494 — whether a members-only school/trip also admits non-members
  // (controls the ΕΟΟΑ guest gating in BookingPanel).
  open_to_non_members: !!t.open_to_non_members,
  // Iter164 — surfaces the archived flag so detail-page Back buttons can
  // route to /usefull/archive instead of the live /expeditions list when
  // the trip is no longer running.
  archived: !!t.archived,
});
};

/**
 * useTrips(kind?) — fetches /api/trips and returns adapted display trips.
 */
export const useTrips = (kind, lang = 'en') => {
  const [trips, setTrips] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const url = `${API}/api/trips${kind ? `?kind=${kind}` : ''}`;
      const r = await fetch(url, { credentials: 'include' });
      if (!r.ok) throw new Error('Failed to load trips');
      const data = await r.json();
      setTrips((data.trips || []).map((t) => adaptTrip(t, lang)));
    } catch (e) {
      setError(e.message);
      setTrips([]);
    }
  }, [kind, lang]);

  useEffect(() => { load(); }, [load]);

  return { trips, error, reload: load };
};

/**
 * useTrip(tripId) — fetches a single trip by id.
 */
export const useTrip = (tripId, lang = 'en') => {
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!tripId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/api/trips/${tripId}`, { credentials: 'include' });
        if (!r.ok) throw new Error(r.status === 404 ? 'not_found' : 'load_error');
        const data = await r.json();
        if (!cancelled) setTrip(adaptTrip(data, lang));
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tripId, lang]);

  return { trip, loading, error };
};
